import React from 'react';

import type { mainListingInfo } from 'apis/handler/listing/listingSchema';
import { FileTextIcon, LocationIcon } from 'entities/home';
import { PiggyBank } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { convertSubwayStationKorToEng, formatPriceOnlyK, formatPriceOnlyM, getListingThumbUrl } from 'shared/lib';
import { PriceWithForeign } from 'shared/ui';

type PropertyCardProps = {
  listing: mainListingInfo;
  onClick: () => void;
};

export const PropertyCard = ({ listing, onClick }: PropertyCardProps) => {
  const { t, i18n } = useTranslation(['listing', 'home']);
  const { lang } = useParams<{ lang?: string }>();

  // 크롤러가 따라갈 수 있는 실제 상세 페이지 주소 (언어 prefix 포함). 클릭 시엔 기존 SPA 이동/GTM 유지.
  const detailHref = lang ? `/${lang}/listing/${listing.id}` : `/listing/${listing.id}`;

  const depositText =
    listing.deposit === 0
      ? t('listing:item.noDeposit')
      : `${t('listing:item.deposit')} ₩${formatPriceOnlyM(listing.deposit, i18n.language)}`;

  const rentText =
    listing.paymentType === 'LUMP_SUM'
      ? t('listing:item.noMonthlyRent')
      : `₩${formatPriceOnlyK(listing.monthlyRent, i18n.language)}`;

  const stationInfo =
    listing.nearSubwayStationInfos.length > 0
      ? t('listing:item.stationDistance', {
          duration: listing.nearSubwayStationInfos[0].duration,
          station: convertSubwayStationKorToEng(listing.nearSubwayStationInfos[0].subwayStation ?? ''),
        })
      : null;

  return (
    <a
      href={detailHref}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        onClick();
      }}
      className="flex w-full cursor-pointer flex-col gap-y-3 md:gap-y-6"
    >
      <div className="h-[180px] w-full rounded-[14px] bg-gray-20 md:h-[300px] md:max-w-[299px] md:rounded-[20px]">
        <img
          src={getListingThumbUrl(listing.imageUrls[0], 800)}
          alt={`${t(`listing:detail.room.${listing.roomType}`)}${listing.area ? ` · ${listing.area}㎡` : ''}${stationInfo ? ` · ${stationInfo}` : ''}`}
          loading="lazy"
          className="h-full w-full rounded-[14px] object-cover md:rounded-[20px]"
          onError={(e) => {
            // 리사이즈 프록시 실패 시 원본으로 1회만 폴백 (브라우저의 URL 정규화 때문에
            // 문자열 비교는 무한 재시도가 될 수 있어 플래그로 판단)
            if (listing.imageUrls[0] && !e.currentTarget.dataset.triedOriginal) {
              e.currentTarget.dataset.triedOriginal = 'true';
              e.currentTarget.src = listing.imageUrls[0];
              return;
            }
            e.currentTarget.style.display = 'none';
          }}
        />
      </div>
      <div className="flex flex-col gap-y-1.5 md:gap-y-2.5 md:px-2.5">
        <div className="flex flex-col gap-y-1.5 md:gap-y-2.5">
          <div className="flex w-fit items-center gap-x-1.5 rounded-[30px] bg-gray-10 px-2 py-0.5 md:gap-x-2 md:px-3 md:py-1">
            <PiggyBank className="h-3.5 w-3.5 md:h-[18px] md:w-[18px]" />
            <span className="text-gray-90 text-[12px] font-medium leading-[1.5] md:text-[18px]">
              <span className="whitespace-nowrap">{depositText}</span>
              {listing.deposit !== 0 && (
                <PriceWithForeign krw={listing.deposit} className="ml-2 text-[11px] font-normal md:text-[14px]" />
              )}
            </span>
          </div>

          <div className="flex flex-wrap items-baseline gap-x-1.5 md:gap-x-2">
            <span className="whitespace-nowrap">
              <span className="text-[17px] font-bold leading-[1.4] text-black md:text-[24px]">{rentText}</span>
              {listing.paymentType !== 'LUMP_SUM' && (
                <span className="ml-1.5 text-[13px] font-medium leading-[1.4] text-gray-60 md:ml-2 md:text-[20px]">
                  {t('listing:item.perMonth')}
                </span>
              )}
            </span>
            {listing.paymentType !== 'LUMP_SUM' && (
              <PriceWithForeign
                krw={listing.monthlyRent}
                suffix={t('listing:item.perMonth')}
                className="ml-0 text-[12px] font-normal md:text-[16px]"
              />
            )}
          </div>
        </div>

        <div className="flex flex-col gap-y-0.5">
          {stationInfo && (
            <div className="flex items-start gap-x-1.5 md:gap-x-2">
              <LocationIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-70 md:mt-1 md:h-[18px] md:w-[18px]" />
              <span className="text-[12px] font-medium leading-[1.5] text-gray-70 md:text-[18px]">{stationInfo}</span>
            </div>
          )}
          <div className="flex items-center gap-x-1.5 md:gap-x-2">
            <FileTextIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-70 md:h-[18px] md:w-[18px]" />
            <span className="text-[12px] font-medium leading-[1.5] text-gray-70 md:text-[18px]">
              {t('listing:item.atLeast')} {listing.minContractPeriod} {t('listing:item.months')}
            </span>
          </div>
        </div>
      </div>
    </a>
  );
};
